import { RefObject, useEffect, useState } from 'react';
import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react';

import { TARGET_ATTRIBUTE } from './overflowTooltipTarget';

type OverflowTooltipState = { anchor: HTMLElement; text: string };

const SPACING = 5;

// One tooltip per table, fed by delegated listeners, so a cell adds no listener or observer of its own.
export const OverflowTooltip = ({ containerRef }: { containerRef: RefObject<HTMLElement | null> }) => {
  const [tooltip, setTooltip] = useState<OverflowTooltipState | null>(null);
  const isOpen = tooltip !== null;

  const { refs, floatingStyles } = useFloating({
    placement: 'top',
    open: isOpen,
    elements: { reference: tooltip?.anchor ?? null },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(SPACING),
      flip({ fallbackAxisSideDirection: 'start', padding: SPACING }),
      shift({ padding: SPACING }),
    ],
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const show = (event: Event) => {
      const next = findCutElement(event.target);
      setTooltip((current) => (next?.anchor === current?.anchor ? current : next));
    };
    const hide = () => setTooltip(null);
    container.addEventListener('pointerover', show);
    container.addEventListener('pointerleave', hide);
    container.addEventListener('input', hide);
    return () => {
      container.removeEventListener('pointerover', show);
      container.removeEventListener('pointerleave', hide);
      container.removeEventListener('input', hide);
    };
  }, [containerRef]);

  // Virtualized rows reuse their cells while scrolling, so the anchor can start showing another row's value.
  useEffect(() => {
    const container = containerRef.current;
    if (!isOpen || !container) {
      return;
    }
    const hide = () => setTooltip(null);
    container.addEventListener('scroll', hide, { passive: true });
    return () => container.removeEventListener('scroll', hide);
  }, [containerRef, isOpen]);

  if (!tooltip) {
    return null;
  }

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        role="tooltip"
        className="pointer-events-none absolute z-100 max-w-60 rounded-sm bg-black px-2 py-1 text-xs text-white"
        style={floatingStyles}
      >
        {tooltip.text}
      </div>
    </FloatingPortal>
  );
};

const findCutElement = (target: EventTarget | null): OverflowTooltipState | null => {
  const anchor = target instanceof Element ? target.closest<HTMLElement>(`input, [${TARGET_ATTRIBUTE}]`) : null;
  if (!anchor || anchor.scrollWidth <= anchor.clientWidth) {
    return null;
  }
  const text = anchor instanceof HTMLInputElement ? anchor.value : anchor.textContent;
  return text ? { anchor, text } : null;
};
